import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { markUserConfigExplainerVideoSeen, type UserConfig } from '@/entities/user-config';
import { StorageKeys } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';

import { userConfigApi } from './userConfig.api';
import { userConfigOptions, userConfigQueryKey } from './userConfig.options';

/**
 * User config is a per-account resource behind auth. Public routes (e.g. the
 * `/:code` factsheet lookup) render for logged-out users too, and this hook is
 * pulled in transitively by display helpers such as `useUserDateFormat`. Without
 * an auth token the request 401/403s and the global interceptor logs the user out
 * to `/expired` — so gate on the gx token to never fire it for a logged-out user
 * (GO-2380). Authenticated callers already hold a gx token, so nothing changes for
 * them.
 */
export const useUserConfig = (enabled = true) => {
  const gxToken = useStorageValue(StorageKeys.GxToken);
  return useQuery({ ...userConfigOptions(), enabled: enabled && Boolean(gxToken) });
};

export const useSaveUserConfig = (userEmail: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userConfig: UserConfig) => {
      const previous = queryClient.getQueryData<UserConfig>(userConfigQueryKey);
      return userConfigApi.save(userConfig, {
        userEmail,
        previousHomepage: previous?.homepage.value ?? ''
      });
    },
    onSuccess: saved => {
      queryClient.setQueryData(userConfigQueryKey, saved);
    }
  });
};

export const useMarkExplainerVideoSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: userConfigQueryKey });
      const previous = queryClient.getQueryData<UserConfig>(userConfigQueryKey);

      if (previous) {
        queryClient.setQueryData(userConfigQueryKey, markUserConfigExplainerVideoSeen(previous));
      }

      return { previous };
    },
    mutationFn: async () => {
      const userConfig = queryClient.getQueryData<UserConfig>(userConfigQueryKey);

      if (!userConfig) throw new Error('No user config in cache');

      return userConfigApi.markExplainerVideoSeen(userConfig);
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(userConfigQueryKey, ctx.previous);
    }
  });
};
